/**
 * useQuiz Hook
 * Main hook for quiz game logic and state management
 */

import { useState, useCallback, useEffect, useMemo } from "react";
import { supabase } from "@/lib/supabase/client";
import { useAuth } from "@/providers/AuthProvider";
import { useGameSession, SessionMode, SessionType, GameResult } from "@/modules/games";
import {
  QuizCategory,
  QuizQuestion,
  QuizGameState,
  QuizSessionSetup,
  SubmitAnswerResult,
  QuizSessionResult,
  DEFAULT_QUIZ_CONFIG,
  QuizConfig,
} from "../types";

interface UseQuizReturn {
  /** Available categories */
  categories: QuizCategory[];
  /** Current question in the session */
  currentQuestion: QuizQuestion | null;
  /** Current question index (0-based) */
  currentIndex: number;
  /** Total questions in session */
  totalQuestions: number;
  /** Current score */
  score: number;
  /** Correct answers count */
  correctCount: number;
  /** Time remaining for current question (seconds) */
  timeRemaining: number;
  /** Whether quiz session is active */
  isPlaying: boolean;
  /** Whether quiz is loading */
  isLoading: boolean;
  /** Whether submitting answer */
  isSubmitting: boolean;
  /** Last answer result */
  lastAnswerResult: SubmitAnswerResult | null;
  /** Session result (when completed) */
  sessionResult: QuizSessionResult | null;
  /** Error message */
  error: string | null;
  /** Quiz configuration */
  config: QuizConfig;
  /** Start a new quiz session */
  startQuiz: (setup: QuizSessionSetup, mode: SessionMode) => Promise<void>;
  /** Submit answer for current question */
  submitAnswer: (optionIndex: number, timeTakenMs: number) => Promise<SubmitAnswerResult | null>;
  /** Move to next question */
  nextQuestion: () => void;
  /** Skip current question (counts as wrong) */
  skipQuestion: () => Promise<void>;
  /** End quiz early */
  endQuiz: () => Promise<QuizSessionResult | null>;
  /** Reset quiz state */
  resetQuiz: () => void;
  /** Refetch categories */
  refetchCategories: () => Promise<void>;
}

interface UseQuizOptions {
  /** Mode for the quiz (weekend competition or free play) */
  mode?: SessionMode;
  /** Auto-fetch categories on mount */
  autoFetch?: boolean;
}

/**
 * Hook for managing quiz game sessions
 */
export const useQuiz = (options: UseQuizOptions = {}): UseQuizReturn => {
  const { mode = "free", autoFetch = true } = options;
  const { activeProfile, activeWeekend } = useAuth();
  const {
    session,
    createSession,
    updateSession,
    completeGame,
    isLoading: isSessionLoading,
  } = useGameSession();

  // State
  const [categories, setCategories] = useState<QuizCategory[]>([]);
  const [questions, setQuestions] = useState<QuizQuestion[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [score, setScore] = useState(0);
  const [correctCount, setCorrectCount] = useState(0);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [lastAnswerResult, setLastAnswerResult] = useState<SubmitAnswerResult | null>(null);
  const [sessionResult, setSessionResult] = useState<QuizSessionResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [answers, setAnswers] = useState<QuizGameState["answers"]>([]);

  // Config from session or default
  const config = useMemo(() => {
    if (session?.game_state?.config) {
      return { ...DEFAULT_QUIZ_CONFIG, ...session.game_state.config };
    }
    return DEFAULT_QUIZ_CONFIG;
  }, [session?.game_state?.config]);

  // Current question
  const currentQuestion = useMemo(() => {
    if (currentIndex >= 0 && currentIndex < questions.length) {
      return questions[currentIndex];
    }
    return null;
  }, [questions, currentIndex]);

  /**
   * Fetch categories
   */
  const fetchCategories = useCallback(async () => {
    const { data, error: fetchError } = await supabase
      .from("quiz_categories")
      .select("*")
      .eq("is_active", true)
      .order("sort_order", { ascending: true });

    if (fetchError) {
      console.error("Error fetching categories:", fetchError);
      return;
    }

    setCategories(data as QuizCategory[]);
  }, []);

  /**
   * Fetch random questions for a session
   */
  const fetchQuestions = useCallback(
    async (
      count: number,
      categoryId?: string | null,
      difficulty?: string | null
    ) => {
      const { data, error: fetchError } = await supabase.rpc(
        "get_random_quiz_questions",
        {
          p_count: count,
          p_category_id: categoryId || null,
          p_difficulty: difficulty || null,
          p_exclude_ids: [],
        }
      );

      if (fetchError) {
        throw new Error(fetchError.message);
      }

      return data as QuizQuestion[];
    },
    []
  );

  /**
   * Fetch questions for a specific quiz
   */
  const fetchQuestionsForQuiz = useCallback(
    async (quizId: string, count: number) => {
      // First get the quiz config
      const { data: quizData, error: quizError } = await supabase
        .from("quizzes")
        .select("*")
        .eq("id", quizId)
        .single();

      if (quizError) {
        throw new Error(quizError.message);
      }

      // Fetch questions for this quiz
      const { data: questionsData, error: questionsError } = await supabase
        .from("quiz_questions")
        .select("*")
        .eq("quiz_id", quizId)
        .eq("is_active", true)
        .order("created_at", { ascending: true });

      if (questionsError) {
        throw new Error(questionsError.message);
      }

      let questions = questionsData as QuizQuestion[];

      // Randomize if configured
      if (quizData.randomize_questions && questions.length > 0) {
        questions = questions.sort(() => Math.random() - 0.5);
      }

      // Limit to count
      if (questions.length > count) {
        questions = questions.slice(0, count);
      }

      return {
        questions,
        quizConfig: quizData,
      };
    },
    []
  );

  /**
   * Start a new quiz session
   */
  const startQuiz = useCallback(
    async (setup: QuizSessionSetup, sessionMode: SessionMode) => {
      if (!activeProfile?.id) {
        setError("Je moet ingelogd zijn om te spelen");
        return;
      }

      setIsLoading(true);
      setError(null);
      setSessionResult(null);
      setLastAnswerResult(null);
      setAnswers([]);
      setScore(0);
      setCorrectCount(0);

      try {
        let fetchedQuestions: QuizQuestion[];
        let effectiveConfig = config;

        // Fetch questions based on quiz_id or category
        if (setup.quiz_id) {
          // Fetch questions for a specific quiz
          const result = await fetchQuestionsForQuiz(
            setup.quiz_id,
            setup.question_count || 10
          );
          fetchedQuestions = result.questions;
          
          // Use quiz-specific config
          if (result.quizConfig) {
            effectiveConfig = {
              ...config,
              default_time_limit: result.quizConfig.time_per_question,
              points_correct: result.quizConfig.points_per_correct,
              points_bonus_speed: result.quizConfig.bonus_speed,
            };
          }
        } else {
          // Fetch random questions by category
          const questionCount = setup.question_count || config.questions_per_session;
          fetchedQuestions = await fetchQuestions(
            questionCount,
            setup.category_id,
            setup.difficulty
          );
        }

        if (fetchedQuestions.length === 0) {
          throw new Error("Geen vragen beschikbaar voor deze selectie");
        }

        // Create game state
        const gameState: QuizGameState = {
          question_ids: fetchedQuestions.map((q) => q.id),
          current_index: 0,
          total_questions: fetchedQuestions.length,
          category_id: setup.category_id || null,
          difficulty: setup.difficulty || null,
          time_per_question: setup.time_per_question || effectiveConfig.default_time_limit,
          total_score: 0,
          correct_count: 0,
          answers: [],
        };

        // Create session
        await createSession({
          game_slug: "quiz",
          mode: sessionMode,
          session_type: "solo" as SessionType,
          weekend_id: sessionMode === "weekend" ? activeWeekend?.id : undefined,
          quiz_id: setup.quiz_id || undefined,
          initial_state: gameState as unknown as Record<string, unknown>,
        });

        // Set local state
        setQuestions(fetchedQuestions);
        setCurrentIndex(0);
        setTimeRemaining(gameState.time_per_question);
        setIsPlaying(true);
      } catch (err) {
        console.error("Error starting quiz:", err);
        setError(err instanceof Error ? err.message : "Fout bij starten quiz");
      } finally {
        setIsLoading(false);
      }
    },
    [activeProfile?.id, activeWeekend?.id, config, createSession, fetchQuestions]
  );

  /**
   * Submit answer for current question
   */
  const submitAnswer = useCallback(
    async (optionIndex: number, timeTakenMs: number): Promise<SubmitAnswerResult | null> => {
      if (!session?.id || !currentQuestion) {
        return null;
      }

      setIsSubmitting(true);
      setLastAnswerResult(null);

      try {
        const { data, error: submitError } = await supabase.rpc(
          "submit_quiz_answer",
          {
            p_session_id: session.id,
            p_question_id: currentQuestion.id,
            p_selected_option_index: optionIndex,
            p_text_answer: null,
            p_time_taken_ms: timeTakenMs,
          }
        );

        if (submitError) {
          throw new Error(submitError.message);
        }

        const result = data as SubmitAnswerResult;

        if (result.error) {
          throw new Error(result.error);
        }

        // Update local state
        const newAnswer = {
          question_id: currentQuestion.id,
          is_correct: result.is_correct,
          points_earned: result.points_earned,
          time_taken_ms: timeTakenMs,
        };

        setAnswers((prev) => [...prev, newAnswer]);
        setScore((prev) => prev + result.points_earned);
        if (result.is_correct) {
          setCorrectCount((prev) => prev + 1);
        }
        setLastAnswerResult(result);

        // Update session game state
        const currentGameState = session.game_state as unknown as QuizGameState;
        const newGameState: QuizGameState = {
          ...currentGameState,
          current_index: currentIndex,
          total_score: score + result.points_earned,
          correct_count: correctCount + (result.is_correct ? 1 : 0),
          answers: [...answers, newAnswer],
        };

        await updateSession({
          game_state: newGameState as unknown as Record<string, unknown>,
        });

        return result;
      } catch (err) {
        console.error("Error submitting answer:", err);
        setError(err instanceof Error ? err.message : "Fout bij verzenden antwoord");
        return null;
      } finally {
        setIsSubmitting(false);
      }
    },
    [session?.id, session?.game_state, currentQuestion, currentIndex, score, correctCount, answers, updateSession]
  );

  /**
   * Move to next question
   */
  const nextQuestion = useCallback(() => {
    if (currentIndex < questions.length - 1) {
      setCurrentIndex((prev) => prev + 1);
      setLastAnswerResult(null);
      const gameState = session?.game_state as unknown as QuizGameState | undefined;
      setTimeRemaining(
        gameState?.time_per_question || config.default_time_limit
      );
    } else {
      // Quiz finished - calculate results
      const result: QuizSessionResult = {
        total_questions: questions.length,
        correct_answers: correctCount,
        wrong_answers: questions.length - correctCount,
        total_points: score,
        accuracy_percentage:
          questions.length > 0
            ? Math.round((correctCount / questions.length) * 100)
            : 0,
        average_time_ms:
          answers.length > 0
            ? Math.round(
                answers.reduce((sum, a) => sum + a.time_taken_ms, 0) /
                  answers.length
              )
            : 0,
        fastest_answer_ms:
          answers.length > 0
            ? Math.min(...answers.map((a) => a.time_taken_ms))
            : 0,
        best_streak: calculateBestStreak(answers),
      };

      setSessionResult(result);
      setIsPlaying(false);

      // End session
      if (session?.id && activeProfile?.id) {
        const gameResult: GameResult = {
          final_scores: { [activeProfile.id]: result.total_points },
          stats: result as unknown as Record<string, unknown>,
          duration_ms: result.average_time_ms * result.total_questions,
        };
        completeGame(gameResult, result.total_points);
      }
    }
  }, [
    currentIndex,
    questions.length,
    correctCount,
    score,
    answers,
    session?.id,
    session?.game_state,
    config.default_time_limit,
    completeGame,
    activeProfile?.id,
  ]);

  /**
   * Skip current question
   */
  const skipQuestion = useCallback(async () => {
    if (!currentQuestion) return;

    // Submit as wrong answer
    const newAnswer = {
      question_id: currentQuestion.id,
      is_correct: false,
      points_earned: 0,
      time_taken_ms: currentQuestion.time_limit_seconds * 1000,
    };

    setAnswers((prev) => [...prev, newAnswer]);
    setLastAnswerResult({
      answer_id: "",
      is_correct: false,
      points_earned: 0,
      correct_option_index: currentQuestion.options.findIndex(
        (o) => o.is_correct
      ),
      explanation: currentQuestion.explanation,
    });

    // Move to next after a short delay
    setTimeout(() => nextQuestion(), 1500);
  }, [currentQuestion, nextQuestion]);

  /**
   * End quiz and return result
   */
  const endQuiz = useCallback(async (): Promise<QuizSessionResult | null> => {
    // Use questions.length as total if we haven't answered all yet
    const totalQuestionsCount = questions.length > 0 ? questions.length : currentIndex + 1;
    const answeredCount = answers.length;
    
    const result: QuizSessionResult = {
      total_questions: totalQuestionsCount,
      correct_answers: correctCount,
      wrong_answers: answeredCount - correctCount,
      total_points: score,
      accuracy_percentage:
        answeredCount > 0
          ? Math.round((correctCount / answeredCount) * 100)
          : 0,
      average_time_ms:
        answers.length > 0
          ? Math.round(
              answers.reduce((sum, a) => sum + a.time_taken_ms, 0) /
                answers.length
            )
          : 0,
      fastest_answer_ms:
        answers.length > 0 ? Math.min(...answers.map((a) => a.time_taken_ms)) : 0,
      best_streak: calculateBestStreak(answers),
    };

    setSessionResult(result);
    setIsPlaying(false);

    if (session?.id && activeProfile?.id) {
      const gameResult: GameResult = {
        final_scores: { [activeProfile.id]: result.total_points },
        stats: result as unknown as Record<string, unknown>,
        duration_ms: result.average_time_ms * result.total_questions,
      };
      await completeGame(gameResult, result.total_points);
    }

    return result;
  }, [questions.length, currentIndex, correctCount, score, answers, session?.id, completeGame, activeProfile?.id]);

  /**
   * Reset quiz state
   */
  const resetQuiz = useCallback(() => {
    setQuestions([]);
    setCurrentIndex(0);
    setScore(0);
    setCorrectCount(0);
    setTimeRemaining(0);
    setIsPlaying(false);
    setLastAnswerResult(null);
    setSessionResult(null);
    setAnswers([]);
    setError(null);
  }, []);

  /**
   * Calculate best streak of correct answers
   */
  const calculateBestStreak = (
    answerList: QuizGameState["answers"]
  ): number => {
    let bestStreak = 0;
    let currentStreak = 0;

    for (const answer of answerList) {
      if (answer.is_correct) {
        currentStreak++;
        bestStreak = Math.max(bestStreak, currentStreak);
      } else {
        currentStreak = 0;
      }
    }

    return bestStreak;
  };

  // Auto-fetch categories on mount
  useEffect(() => {
    if (autoFetch) {
      fetchCategories();
    }
  }, [autoFetch, fetchCategories]);

  // Timer countdown
  useEffect(() => {
    if (!isPlaying || timeRemaining <= 0 || lastAnswerResult) {
      return;
    }

    const timer = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 1) {
          // Time's up - skip question
          skipQuestion();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isPlaying, timeRemaining, lastAnswerResult, skipQuestion]);

  return {
    categories,
    currentQuestion,
    currentIndex,
    totalQuestions: questions.length,
    score,
    correctCount,
    timeRemaining,
    isPlaying,
    isLoading: isLoading || isSessionLoading,
    isSubmitting,
    lastAnswerResult,
    sessionResult,
    error,
    config,
    startQuiz,
    submitAnswer,
    nextQuestion,
    skipQuestion,
    endQuiz,
    resetQuiz,
    refetchCategories: fetchCategories,
  };
};

